import React, { useState } from 'react';
import { T, font, mono, COMPANY } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, SearchBar, StatusBadge, SBadge, ViewToggle, IcCheck, IcPrint, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';

export default function SupplierAssigned({ lang, supplier, products, reload, Lk }) {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState('table');
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(null);

  const filt = p => !search || matchesSearch(search, p.name) || matchesSearch(search, p.sku || '');
  const assigned = products.filter(p => p.status === 'supplier_assigned' && p.assigned_supplier === supplier.id).filter(filt);
  const accepted = products.filter(p => p.status === 'ordering' && p.assigned_supplier === supplier.id && !p.shipment_status).filter(filt);
  const allAccepted = products.filter(p => p.status === 'ordering' && p.assigned_supplier === supplier.id && !p.shipment_status);

  const doAcknowledge = async (pid) => {
    if (confirming) return;
    setConfirming(pid);
    await api.acknowledgeProduct(pid);
    await reload();
    setConfirming(null);
  };

  const doPrintAccepted = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Accepted Orders</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#333}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#222;color:#fff;padding:10px 14px;text-align:left;font-size:13px}td{padding:8px 14px;border-bottom:1px solid #ddd;font-size:13px}.header{display:flex;justify-content:space-between;margin-bottom:30px}.info{text-align:right;font-size:12px;line-height:1.6}</style></head><body>`);
    w.document.write(`<div class="header"><div><strong>${COMPANY.name}</strong></div><div class="info"><strong>${COMPANY.legal}</strong><br>${COMPANY.addr}<br>${COMPANY.city}<br>${COMPANY.phone}<br>${COMPANY.email}<br>${COMPANY.web}</div></div>`);
    w.document.write(`<div style="font-size:11px;color:#888;margin-bottom:4px">${new Date().toLocaleString()}</div>`);
    w.document.write(`<div style="font-size:18px;font-weight:bold;margin-bottom:10px">ACCEPTED ORDERS — ${supplier.name}</div>`);
    w.document.write(`<table><thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Price</th><th>Sub Total</th></tr></thead><tbody>`);
    accepted.forEach(p => {
      const q = p.quotes?.[supplier.id];
      const price = q?.price || 0;
      const qty = p.assigned_qty || p.qty_ordered || 0;
      w.document.write(`<tr><td>${p.name}</td><td>${p.sku || '—'}</td><td>${qty}</td><td>${money(price)}</td><td>${money((price * qty))}</td></tr>`);
    });
    w.document.write(`</tbody></table></body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('assignedOrders')}</h2>
        <ViewToggle mode={viewMode} setMode={setViewMode} lang={lang} />
      </div>

      <SearchBar sticky value={search} onChange={setSearch} placeholder={Lk('searchProduct')} style={{ marginBottom: 16 }} />

      {assigned.length === 0 && accepted.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{lang === 'zh' ? '目前没有分配给您的订单。' : 'No orders assigned to you.'}</div>
      ) : null}

      {/* Awaiting confirmation */}
      {assigned.length > 0 && (
        <Sect title={Lk('awaitingConfirmation')}>
          {(viewMode === 'table' && !isMobile) ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
              <thead><tr>{[Lk('productName'), Lk('sku'), Lk('price'), Lk('qty'), Lk('status'), Lk('action')].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {assigned.map(p => {
                  const q = p.quotes?.[supplier.id];
                  return (
                    <tr key={p.id}>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>{p.name}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.warn }}>{q ? `${money(q.price)}` : '—'}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.warn }}>{p.assigned_qty || p.qty_ordered || '—'}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}><SBadge label={Lk('awaitingConfirmation')} color={T.warn} bg={T.warnBg} /></td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                        <Btn variant="small" onClick={() => doAcknowledge(p.id)} disabled={confirming === p.id}>{IcCheck} {Lk('confirmAccept')}</Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : assigned.map(p => {
            const q = p.quotes?.[supplier.id];
            return (
              <Card key={p.id} style={{ marginBottom: 8, borderColor: T.warn + '50' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{p.name}</span>
                    <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{p.sku}</span>
                    {q && <span style={{ fontFamily: mono, fontSize: 13, color: T.warn, marginLeft: 10 }}>{money(q.price)}</span>}
                  </div>
                  <SBadge label={Lk('awaitingConfirmation')} color={T.warn} bg={T.warnBg} />
                </div>
                <Btn onClick={() => doAcknowledge(p.id)} disabled={confirming === p.id} style={{ width: '100%', justifyContent: 'center' }}>{IcCheck} {Lk('confirmAccept')}</Btn>
              </Card>
            );
          })}
        </Sect>
      )}

      {/* Accepted orders */}
      {accepted.length > 0 && (
        <Sect title={Lk('acceptedOrders')} right={<Btn variant="ghostSm" onClick={doPrintAccepted}>{IcPrint} {Lk('print')}</Btn>}>
          {(viewMode === 'table' && !isMobile) ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
              <thead><tr>{[Lk('productName'), Lk('sku'), Lk('price'), Lk('qty'), Lk('status'), Lk('dateChanged')].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {accepted.map(p => {
                  const q = p.quotes?.[supplier.id];
                  return (
                    <tr key={p.id}>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>{p.name}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.purple }}>{q ? `${money(q.price)}` : '—'}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.purple }}>{p.assigned_qty || p.qty_ordered || '—'}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}><StatusBadge status="ordering" lang={lang} /></td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.dim, fontFamily: mono }}>{p.acknowledged_at || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : accepted.map(p => {
            const q = p.quotes?.[supplier.id];
            return (
              <Card key={p.id} style={{ marginBottom: 6, background: T.purpleBg, borderColor: T.purple + '30' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{p.name}</span>
                    <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{p.sku}</span>
                  </div>
                  {q && <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, color: T.purple }}>{money(q.price)}</span>}
                  <StatusBadge status="ordering" lang={lang} />
                </div>
              </Card>
            );
          })}
        </Sect>
      )}
    </div>
  );
}
